import { useState, useEffect, useMemo, useCallback } from 'react';
import { getOrgMembers, type OrgMember } from '@/db/organizations';
import { useOrganization } from '@/context/OrganizationContext';

/**
 * Hook that loads org members and provides:
 * - orgMemberNames: display names of all org members
 * - availableAssignees: merged set of org members + any extra names (e.g. from tasks)
 */
export function useOrgMembers(_extraNames?: string[]) {
  const { currentOrg } = useOrganization();
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchMembers = useCallback(() => {
    if (currentOrg?.id) {
      return getOrgMembers(currentOrg.id).then(m => { setOrgMembers(m); setLoaded(true); }).catch(() => { setOrgMembers([]); setLoaded(true); });
    }
    return Promise.resolve();
  }, [currentOrg?.id]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const orgMemberNames = useMemo(() => {
    return orgMembers.map(m => m.display_name || m.email || m.user_id.slice(0, 8));
  }, [orgMembers]);

  // Set of all identifiers that can match a current org member:
  // display_name, email, user_id (full + short prefix), all lowercased
  const orgMemberIdentifiers = useMemo(() => {
    const ids = new Set<string>();
    for (const m of orgMembers) {
      if (m.display_name) ids.add(m.display_name.toLowerCase());
      if (m.email) ids.add(m.email.toLowerCase());
      ids.add(m.user_id.toLowerCase());
      ids.add(m.user_id.slice(0, 8).toLowerCase());
    }
    return ids;
  }, [orgMembers]);

  const isCurrentOrgMember = useMemo(() => {
    return (nameOrId: string) => !loaded || orgMemberIdentifiers.has(nameOrId.toLowerCase());
  }, [orgMemberIdentifiers, loaded]);

  /** user_ids of current org members — use these for new task assignments */
  const availableAssigneeIds = useMemo(() => {
    return orgMembers.map(m => m.user_id);
  }, [orgMembers]);

  /** UUID-based list for assignee selectors (matches what's stored in task.assignees) */
  const availableAssignees = useMemo(() => {
    return orgMembers.map(m => m.user_id);
  }, [orgMembers]);

  /** Map from user_id → display name for quick resolution */
  const resolveDisplayName = useCallback(
    (idOrName: string): string => {
      const member = orgMembers.find(m => m.user_id === idOrName);
      if (member) return member.display_name || member.email || member.user_id.slice(0, 8);
      return idOrName;
    },
    [orgMembers]
  );

  return { orgMembers, orgMemberNames, orgMemberIdentifiers, isCurrentOrgMember, availableAssignees, availableAssigneeIds, resolveDisplayName, refetchMembers: fetchMembers, loaded };
}
